import { JSX, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import { MAX_QIT_IDS_FROM_PARAMS } from 'src/redux/slices/Interview/types';
import InterviewShell from './InterviewShell';

/**
 * InterviewReAnswerRoute — boots a scoped interview for re-answering
 * selected questions from the interview complete page. Returns to the
 * complete page when the scoped session finishes.
 *
 * Entry: /renter/interview/:surveyId/answer?qits=1,2,3
 */
export default function InterviewReAnswerRoute(): JSX.Element | null {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { callIPE } = useIPE();

  const parsedSurveyId = surveyId ? parseInt(surveyId, 10) : null;

  // Parse ?qits=1,2,3
  const qitsParam = searchParams.get('qits');
  const qitIds: number[] = qitsParam
    ? qitsParam
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n))
        .slice(0, MAX_QIT_IDS_FROM_PARAMS)
    : [];

  // Parse ?answer_group_qits=100:111-222
  const agParam = searchParams.get('answer_group_qits');
  const answerGroupQits: Record<number, number[]> = {};
  agParam?.split(',').forEach((chunk) => {
    const trimmed = chunk.trim();
    if (trimmed.includes(':')) {
      const [agStr, qitsStr] = trimmed.split(':');
      const agId = parseInt(agStr, 10);
      if (!isNaN(agId)) {
        const qitIds = qitsStr
          .split('-')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !isNaN(n));
        if (qitIds.length > 0) {
          answerGroupQits[agId] = qitIds;
        }
      }
    }
  });

  const allQitIdsSet = new Set(qitIds);
  for (const qitIds of Object.values(answerGroupQits)) {
    for (const id of qitIds) {
      allQitIdsSet.add(id);
    }
  }
  const onlyQitIds = allQitIdsSet.size > 0 ? Array.from(allQitIdsSet) : [];

  const backPath = `/renter/interview/${surveyId}/complete`;

  useEffect(() => {
    if (!parsedSurveyId) {
      return;
    }
    dispatch(interviewActions.clearAllDeferred());
    dispatch(interviewActions.deferFileUpload());
    dispatch(
      interviewActions.initInterview({
        surveyId: parsedSurveyId,
        onlyQitIds: onlyQitIds.length > 0 ? onlyQitIds : null,
        answerGroupQits:
          Object.keys(answerGroupQits).length > 0 ? answerGroupQits : null,
        backPath,
      })
    );
    callIPE({
      surveyIdOverride: parsedSurveyId,
      onlyQitIds: onlyQitIds.length > 0 ? onlyQitIds : undefined,
      answerGroupQits:
        Object.keys(answerGroupQits).length > 0 ? answerGroupQits : undefined,
    });
    return () => {
      dispatch(interviewActions.resetInterview());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedSurveyId]);

  if (!parsedSurveyId) {
    return null;
  }

  return <InterviewShell />;
}
